import { ReactNode } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { scaleHover, pulseGlow } from '@/animations/variants';

interface ButtonProps extends Omit<HTMLMotionProps<"button">, 'children'> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  isLoading?: boolean;
}

const buttonVariants = {
  primary: 'bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-glow',
  secondary: 'bg-secondary hover:bg-secondary/90 text-primary shadow-lg hover:shadow-glow-powder',
  outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-white',
  ghost: 'text-primary hover:bg-primary/10',
};

const buttonSizes = {
  sm: 'px-3 py-2 text-xs sm:text-sm',
  md: 'px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base',
  lg: 'px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg',
};

export default function Button({ 
  children, 
  variant = 'primary', 
  size = 'md',
  fullWidth = false,
  isLoading = false,
  className = '',
  ...props 
}: ButtonProps) {
  return (
    <motion.button
      className={`
        ${buttonVariants[variant]}
        ${buttonSizes[size]}
        ${fullWidth ? 'w-full' : ''}
        font-heading font-semibold rounded-lg
        transition-all duration-fast
        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
      variants={scaleHover}
      whileHover="whileHover"
      whileTap="whileTap"
      disabled={isLoading}
      animate={variant === 'primary' ? pulseGlow : {}}
      {...props}
    >
      {isLoading ? (
        <div className="flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
          Loading...
        </div>
      ) : (
        children
      )}
    </motion.button>
  );
}